import { decodeHtmlEntities } from '@/utils/forum-html'

export interface ForumMentionRange {
  userId: number
  profileId: string
  displayName: string
  start: number
  end: number
}

export type ForumRichPart =
  | { type: 'text'; value: string }
  | { type: 'hashtag'; value: string }
  | { type: 'image'; value: string }
  | { type: 'mention'; profileId: string; label: string }

const FORUM_MENTION_SPAN_WITH_OPTIONAL_AT_REGEX =
  /@?<span[^>]*class="[^"]*mention[^"]*"[^>]*(?:data-profile-id|data-mention-id)="([^"]+)"[^>]*>([\s\S]*?)<\/span>/gi
const FORUM_MENTION_ANCHOR_WITH_OPTIONAL_AT_REGEX =
  /@?<a[^>]*class="[^"]*mention[^"]*"[^>]*href=["'][^"']*\/([^/"']+)["'][^>]*>([\s\S]*?)<\/a>/gi

function stripMentionLabel(value: string): string {
  return decodeHtmlEntities(value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim())
}

function decodeForumRichTextSegment(value: string): string {
  return decodeHtmlEntities(
    value
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<[^>]+>/g, '')
  )
}

function pushForumTextPart(parts: ForumRichPart[], value: string): void {
  const text = decodeForumRichTextSegment(value)
  if (!text) {
    return
  }

  const last = parts[parts.length - 1]
  if (last?.type === 'text') {
    last.value += text
    return
  }

  parts.push({ type: 'text', value: text })
}

function buildForumMentionToken(profileId: string, label: string): string {
  return `@@MENTION@@${profileId}@@LABEL@@${label}@@/MENTION@@`
}

function collapseForumMentionTokenDelimiters(text: string): string {
  return text
    .replace(/@{3,}MENTION@{2,}/gi, '@@MENTION@@')
    .replace(/@@MENTION@@+/g, '@@MENTION@@')
    .replace(/@@LABEL@@+/g, '@@LABEL@@')
    .replace(/@@\/MENTION@@+/g, '@@/MENTION@@')
    .replace(/@@LABEL@@\s*\n\s*@@/g, '@@LABEL@@@')
}

function preprocessForumRichSource(source: string): string {
  return source
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    .replace(/<p[^>]*>/gi, '')
    .replace(/<\/p>/gi, '\n')
}

function replaceForumMentionMarkup(
  rawContent: string,
  spanPattern: RegExp,
  anchorPattern: RegExp
): string {
  let normalized = preprocessForumRichSource(rawContent)

  const injectMentionToken = (
    whole: string,
    match: string,
    profileId: string,
    inner: string,
    offset: number
  ): string => {
    const label = stripMentionLabel(inner) || `@${profileId}`
    let token = buildForumMentionToken(profileId, label)

    const before = offset > 0 ? whole[offset - 1] : ''
    const afterIndex = offset + match.length
    const after = afterIndex < whole.length ? whole[afterIndex] : ''

    const needsLeadingSpace =
      Boolean(before) && !/\s/.test(before) && before !== '@' && before !== '>'
    const needsTrailingSpace =
      Boolean(after) && !/\s/.test(after) && after !== '<' && after !== '@'

    if (needsLeadingSpace) {
      token = ` ${token}`
    }
    if (needsTrailingSpace) {
      token = `${token} `
    }

    return token
  }

  normalized = normalized.replace(
    spanPattern,
    (match, profileId: string, inner: string, offset: number) =>
      injectMentionToken(normalized, match, profileId, inner, offset)
  )
  normalized = normalized.replace(
    anchorPattern,
    (match, profileId: string, inner: string, offset: number) =>
      injectMentionToken(normalized, match, profileId, inner, offset)
  )
  return collapseForumMentionTokenDelimiters(normalized)
}

function normalizeForumMentionTokens(rawContent: string): string {
  return replaceForumMentionMarkup(
    rawContent,
    FORUM_MENTION_SPAN_WITH_OPTIONAL_AT_REGEX,
    FORUM_MENTION_ANCHOR_WITH_OPTIONAL_AT_REGEX
  )
}

function parseForumRichContentFromNormalized(normalized: string): ForumRichPart[] {
  const parts: ForumRichPart[] = []
  const pattern =
    /@@MENTION@@([^@]+?)@@LABEL@@([\s\S]*?)@@\/MENTION@@|<img[^>]+src="([^"]+)"[^>]*>|#(\w+)|[^#@<]+|[#@<]+/gi

  let lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = pattern.exec(normalized)) !== null) {
    if (match.index > lastIndex) {
      pushForumTextPart(parts, normalized.slice(lastIndex, match.index))
    }

    if (match[1] !== undefined && match[2] !== undefined) {
      const profileId = match[1].trim()
      const label = stripMentionLabel(match[2]) || `@${profileId}`
      if (profileId) {
        parts.push({ type: 'mention', profileId, label })
      }
    } else if (match[3]) {
      parts.push({ type: 'image', value: match[3] })
    } else if (match[4]) {
      parts.push({ type: 'hashtag', value: match[4] })
    } else {
      pushForumTextPart(parts, match[0])
    }

    lastIndex = pattern.lastIndex
  }

  if (lastIndex < normalized.length) {
    pushForumTextPart(parts, normalized.slice(lastIndex))
  }

  return parts.filter(
    part =>
      part.type === 'mention' ||
      part.type === 'image' ||
      (part.type === 'text' && part.value.length > 0) ||
      (part.type === 'hashtag' && part.value.trim().length > 0)
  )
}

function parseForumRichContent(rawContent: string): ForumRichPart[] {
  const source = rawContent?.trim()
  if (!source) {
    return []
  }

  const normalized = normalizeForumMentionTokens(source)
  return parseForumRichContentFromNormalized(normalized)
}

/** Plain-text editor state from stored forum HTML (mentions, hashtags, text). */
export function buildForumMentionDraftFromContent(rawContent: string): {
  content: string
  mentionRanges: ForumMentionRange[]
} {
  const parts = parseForumRichContent(rawContent)
  if (!parts.length) {
    return { content: '', mentionRanges: [] }
  }

  let content = ''
  const mentionRanges: ForumMentionRange[] = []

  for (const part of parts) {
    if (part.type === 'text') {
      content += part.value
      continue
    }

    if (part.type === 'hashtag') {
      content += `#${part.value}`
      continue
    }

    if (part.type === 'mention') {
      const label = part.label.startsWith('@') ? part.label : `@${part.label}`
      const start = content.length
      content += label
      mentionRanges.push({
        userId: 0,
        profileId: part.profileId,
        displayName: label.slice(1),
        start,
        end: content.length,
      })
    }
  }

  return { content, mentionRanges }
}

/**
 * Preserve word-boundary spaces around mention tags when HTML stores them flush
 * against adjacent text (e.g. `hello<span class="mention">@Name</span>more`).
 * Keeps the rest of the Quill HTML intact (math blocks, code blocks, etc.).
 */
export function normalizeForumMentionHtmlForEditor(rawContent: string): string {
  const source = rawContent?.trim()
  if (!source) {
    return rawContent
  }

  let normalized = source
  const patterns = [
    FORUM_MENTION_SPAN_WITH_OPTIONAL_AT_REGEX,
    FORUM_MENTION_ANCHOR_WITH_OPTIONAL_AT_REGEX,
  ]

  for (const pattern of patterns) {
    pattern.lastIndex = 0
    normalized = normalized.replace(
      pattern,
      (match, _profileId: string, _inner: string, offset: number) => {
        const before = offset > 0 ? normalized[offset - 1] : ''
        const afterIndex = offset + match.length
        const after = afterIndex < normalized.length ? normalized[afterIndex] : ''

        const needsLeadingSpace =
          Boolean(before) && !/\s/.test(before) && before !== '@' && before !== '>'
        const needsTrailingSpace =
          Boolean(after) && !/\s/.test(after) && after !== '<' && after !== '@'

        let result = match
        if (needsLeadingSpace) {
          result = ` ${result}`
        }
        if (needsTrailingSpace) {
          result = `${result} `
        }
        return result
      }
    )
  }

  return normalized
}
